import { type ReactNode, useRef } from "react";
import { uniqueId } from "lodash";
import InfiniteScroll from "react-infinite-scroll-component";
import { t } from "@/i18n-js/instance";
import type { MarketingEmailTemplate } from "@/react/types/MarketingEmailTemplates";
import { EmptyState } from "@circle-react-uikit/EmptyState";
import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import { LoaderRow } from "../../LoaderRow";
import { useTemplatesInfiniteQuery } from "./hooks/useTemplatesInfiniteQuery";

const localeNamespace = "settings.emails.templates";

export function TemplateGroup({
  children,
}: {
  children: ({
    templates,
  }: {
    templates: MarketingEmailTemplate[];
  }) => ReactNode;
}) {
  const scrollableNodeIdRef = useRef(
    uniqueId("marketing-email-list-templates-body-"),
  );
  const { templates, totalCount, fetchNextPage, hasNextPage, isLoading } =
    useTemplatesInfiniteQuery({
      params: {
        page: 1,
        per_page: 20,
      },
    });

  if (isLoading) {
    return <Loader />;
  }

  const isEmpty = templates.length === 0;
  if (isEmpty) {
    return (
      <EmptyState
        header={t([localeNamespace, "no_saved_templates"])}
        description={t([localeNamespace, "no_saved_templates_description"])}
      />
    );
  }

  return (
    <div className="flex h-full flex-col gap-3.5">
      <div className="flex flex-col gap-y-4 px-5 md:px-8">
        <Typography.TitleMd>
          {t([localeNamespace, "saved_templates"])}
        </Typography.TitleMd>
        <Typography.LabelXs color="text-default">
          {t([localeNamespace, "saved_templates_count"], {
            count: totalCount,
          })}
        </Typography.LabelXs>
      </div>
      <div
        className="flex-1 overflow-y-auto px-5 pb-6 md:px-8"
        id={scrollableNodeIdRef.current}
      >
        <InfiniteScroll
          next={fetchNextPage}
          hasMore={Boolean(hasNextPage)}
          loader={<LoaderRow />}
          dataLength={templates.length}
          scrollableTarget={scrollableNodeIdRef.current}
        >
          <div className="3xl:grid-cols-[repeat(5,_minmax(min(15rem,_100%),_1fr))] grid grid-cols-[repeat(auto-fill,_minmax(min(14rem,_100%),_1fr))] justify-start gap-8">
            {children({ templates })}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
}
